import { Flex } from '@mantine/core';
import type { TFunction } from 'i18next';
import { runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Targeting } from '@feathr/blackbox';
import { Button, EmptyState } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { cssVar } from '@feathr/hooks';
import type { ListResponse } from '@feathr/rachis';

import { determineInterestsMatchMode } from '../Interests';
import {
  getAgeDemographicTargetings,
  getGeoTargetings,
  getInterestsTargetings,
} from '../Targetings.utils';
import RemovableTargeting from './SelectedTargetings.RemovableTargeting';
import TargetingsWell from './SelectedTargetings.TargetingsWell';
interface IProps {
  targetings: ListResponse<Targeting>;
  children?: JSX.Element;
  emptyState?: JSX.Element;
  clearButtonText?: string;
}

function getInterestDescription(targetings: Targeting[], t: TFunction): string {
  // Show nothing if no interests are selected
  if (targetings.length === 0) {
    return '';
  }

  const matchMode = determineInterestsMatchMode(targetings);
  return matchMode === 'match_any' ? t('any of the selected') : t('all of the selected');
}

function SelectedTargetings({
  targetings: targetingsList,
  children,
  emptyState,
  clearButtonText,
}: Readonly<IProps>): JSX.Element {
  const { Targetings } = useStore();
  const { t } = useTranslation();

  const targetings = targetingsList.models;

  function removeTargeting(targeting: Targeting): void {
    runInAction(() => {
      if (!targeting.isEphemeral) {
        targeting.set({ is_archived: true, is_enabled: false });
      } else {
        targetings.remove(targeting);
        Targetings.remove(targeting.id);
      }
    });
  }

  const geoTargetings = getGeoTargetings(targetings);
  const ageDemographicTargetings = getAgeDemographicTargetings(targetings);
  const interestsTargetings = getInterestsTargetings(targetings);

  const allTargetings = [...geoTargetings, ...ageDemographicTargetings, ...interestsTargetings];

  function renderSelectedTargetings(targetings: Targeting[]): JSX.Element[] {
    return targetings.map((targeting): JSX.Element => {
      const name =
        targeting.get('kind') === 'age_demographic'
          ? t('{{ range }} years', { range: targeting.get('name') })
          : targeting.get('name');

      return (
        <RemovableTargeting
          key={targeting.id}
          name={name}
          removeTargeting={removeTargeting}
          targeting={targeting}
        />
      );
    });
  }

  function handleClearAllFilters(): void {
    runInAction(() => {
      allTargetings.forEach((targeting) => {
        removeTargeting(targeting);
      });
    });
  }

  const defaultEmptyState = (
    <EmptyState
      description={t('Get started by selecting some filters')}
      label={t('No filters selected')}
      theme={'slate'}
    />
  );

  return allTargetings.length === 0 ? (
    <>{emptyState ?? defaultEmptyState}</>
  ) : (
    <Flex direction={'column'} gap={cssVar('--spacing-4')}>
      {children}

      <TargetingsWell glue={true} label={t('Locations')} targetings={geoTargetings}>
        {renderSelectedTargetings(geoTargetings)}
      </TargetingsWell>

      <TargetingsWell
        glue={true}
        label={t('Age Demographics')}
        targetings={ageDemographicTargetings}
      >
        {renderSelectedTargetings(ageDemographicTargetings)}
      </TargetingsWell>

      <TargetingsWell
        description={getInterestDescription(interestsTargetings, t)}
        label={t('Interests')}
        targetings={interestsTargetings}
      >
        {renderSelectedTargetings(interestsTargetings)}
      </TargetingsWell>

      {allTargetings.length > 0 && (
        <Button onClick={handleClearAllFilters}>{clearButtonText ?? t('Clear filters')}</Button>
      )}
    </Flex>
  );
}

export default observer(SelectedTargetings);
